import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import {
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
} from '@vi-iot/client';

import { VddDetailsListActions } from './vdd-details-list.actions';
import { RawVddDetails } from './vdd-details-list.model';
import { VddDetailsListSelectors } from './vdd-details-list.selectors';

@Injectable({ providedIn: 'root' })
export class VddDetailsListStore {
  constructor(private store: Store) {}

  isAnyVddLoading$: Observable<boolean> = this.store.select(
    VddDetailsListSelectors.isAnyVddLoading()
  );

  clearVdd(vddName: string, logicalLinkName: string): void {
    this.store.dispatch(VddDetailsListActions.ClearVdd({ vddName, logicalLinkName }));
  }

  isVddLoading(vddName: string, logicalLink: string): Observable<boolean> {
    return this.store.select(VddDetailsListSelectors.isVddLoading(vddName, logicalLink));
  }

  isVddReadInErrorState(vddName: string, logicalLink: string): Observable<boolean> {
    return this.store.select(VddDetailsListSelectors.isVddReadInErrorState(vddName, logicalLink));
  }

  canDisplayVdd(vddName: string, logicalLink: string, isReadVdd: boolean): Observable<boolean> {
    return this.store.select(
      VddDetailsListSelectors.canDisplayVdd(vddName, logicalLink, isReadVdd)
    );
  }

  getVddDetail(vddName: string, logicalLink: string): Observable<RawVddDetails> {
    return this.store.select(VddDetailsListSelectors.getVddDetail(vddName, logicalLink));
  }

  getMatchedValues(
    vddName: string,
    logicalLink: string
  ): Observable<{
    matchedValues: RemoteDiagnosticsVddGenericStructure[];
    rawSingleRead: RemoteDiagnosticsSuccessResponseData;
    flattenedStructure: RemoteDiagnosticsVddGenericStructure[];
    flattenedMetadata: RemoteDiagnosticsVddGenericStructure[];
  }> {
    return this.store.select(VddDetailsListSelectors.getMatchedValues(vddName, logicalLink));
  }

  getVddFlattenedMetadata(
    vddName: string,
    logicalLink: string
  ): Observable<RemoteDiagnosticsVddGenericStructure[]> {
    return this.store.select(VddDetailsListSelectors.getVddFlattenedMetadata(vddName, logicalLink));
  }

  getVddMetadata(vddName: string, gatewaySerial: string, logicalLinkName: string): void {
    this.store.dispatch(
      VddDetailsListActions.GetVddMetadataAttempt({ vddName, gatewaySerial, logicalLinkName })
    );
  }

  getVddStructure(vddName: string, gatewaySerial: string, logicalLinkName: string): void {
    this.store.dispatch(
      VddDetailsListActions.GetVddStructureAttempt({ vddName, gatewaySerial, logicalLinkName })
    );
  }

  getVddSingleValue(vddName: string, gatewaySerial: string, logicalLinkName: string): void {
    this.store.dispatch(
      VddDetailsListActions.GetVddSingleValueAttempt({ vddName, gatewaySerial, logicalLinkName })
    );
  }

  getVddMetadataAndStructureAndValue(
    vddName: string,
    gatewaySerial: string,
    logicalLinkName: string
  ): void {
    this.store.dispatch(
      VddDetailsListActions.GetMetadataAndStructureAndValueAttempt({
        vddName,
        gatewaySerial,
        logicalLinkName,
      })
    );
  }

  getStructureAndValueForVddListAttempt(
    vddNames: string[],
    gatewaySerial: string,
    logicalLinkName: string
  ): void {
    this.store.dispatch(
      VddDetailsListActions.GetStructureAndValueForVddListAttempt({
        vddNames,
        gatewaySerial,
        logicalLinkName,
      })
    );
  }

  getMetadataAndStructureAndValueForVddList(
    vddNames: string[],
    gatewaySerial: string,
    logicalLinkName: string
  ): void {
    this.store.dispatch(
      VddDetailsListActions.GetMetadataAndStructureAndValueForVddListAttempt({
        vddNames,
        gatewaySerial,
        logicalLinkName,
      })
    );
  }

  getIsVddWriting(vddName: string, logicalLink: string): Observable<boolean> {
    return this.store.select(VddDetailsListSelectors.isVddWritingLoading(vddName, logicalLink));
  }

  isAnyVddWriting(): Observable<boolean> {
    return this.store.select(VddDetailsListSelectors.isAnyVddWriting());
  }

  setNewValue(vddName: string, logicalLink: string, path: string, newValue: string): void {
    this.store.dispatch(
      VddDetailsListActions.SetNewValue({
        vddName,
        logicalLink,
        path,
        newValue,
      })
    );
  }

  setRawSingleReadValues(
    vddName: string,
    logicalLink: string,
    rawSingleReadValues: RemoteDiagnosticsSuccessResponseData
  ): void {
    this.store.dispatch(
      VddDetailsListActions.SetRawSingleReadValues({
        vddName,
        logicalLink,
        rawSingleReadValues,
      })
    );
  }

  writeVddSingleValue(vddName: string, logicalLink: string, gatewaySerial: string): void {
    this.store.dispatch(
      VddDetailsListActions.writeVddSingleValue.attempt({
        gatewaySerial,
        vddName,
        logicalLink,
      })
    );
  }

  selectMatchedValues(
    vddName: string,
    logicalLink: string
  ): Observable<RemoteDiagnosticsVddGenericStructure[]> {
    return this.store.select(VddDetailsListSelectors.selectMatchedValues(vddName, logicalLink));
  }
}
